import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  ListItem,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import PlaceIcon from '@mui/icons-material/Place';
import { useTranslation } from 'react-i18next';
import { AccountData, OrderPageData, OrderPageTypes } from '../../api';
import { OrderPageStatus } from '../../api/models/OrderPageStatus';
import { getOrderPages, getPassOrderPages } from '../../utils/AccountUtils';
import { useLanguage } from '../GeneralUtils';
import { getActiveLocations, groupPagesByLocation } from './locationGrouping';

type Props = {
  account: AccountData;
  selectedIds: string[] | null;
  onChange: (next: string[] | null) => void;
  hideLocations?: boolean;
  showEmployeesLocationHint?: boolean;
};

export const WorkshopsFilterSection = ({
  account,
  selectedIds,
  onChange,
  hideLocations,
  showEmployeesLocationHint,
}: Props) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { t } = useTranslation();
  const language = useLanguage();

  const activeOrderPages = getOrderPages(account).filter((op) => op.status !== OrderPageStatus.ARCHIVED);
  const allIds = activeOrderPages.map((op) => op.id);
  const activeLocations = getActiveLocations(account);
  const multiLocation = activeLocations.length > 1;

  const currentIds = selectedIds ?? allIds;

  const setCurrentIds = (next: string[]) => {
    onChange(next.length === allIds.length ? null : next);
  };

  const toggleOrderPage = (id: string, checked: boolean) => {
    const next = checked ? [...currentIds, id] : currentIds.filter((x) => x !== id);
    setCurrentIds(next);
  };

  const toggleLocation = (locationId: string, checked: boolean) => {
    const locationPageIds = activeOrderPages.filter((op) => op.location_id === locationId).map((op) => op.id);
    const next = checked
      ? [...new Set([...currentIds, ...locationPageIds])]
      : currentIds.filter((id) => !locationPageIds.includes(id));
    setCurrentIds(next);
  };

  const isLocationChecked = (locationId: string) => {
    const locationPageIds = activeOrderPages.filter((op) => op.location_id === locationId).map((op) => op.id);
    return locationPageIds.length > 0 && locationPageIds.every((id) => currentIds.includes(id));
  };

  const isLocationIndeterminate = (locationId: string) => {
    const locationPageIds = activeOrderPages.filter((op) => op.location_id === locationId).map((op) => op.id);
    const selectedCount = locationPageIds.filter((id) => currentIds.includes(id)).length;
    return selectedCount > 0 && selectedCount < locationPageIds.length;
  };

  const passRedeemableIds = new Set(
    getPassOrderPages(account).flatMap((pass) => (pass.redeemable_on ?? []).map((r) => r.order_page_id)),
  );

  const privatePages = activeOrderPages.filter((op) => op.order_page_type === OrderPageTypes.PRIVATE);
  const eventPages = activeOrderPages.filter(
    (op) => op.order_page_type === OrderPageTypes.EVENT && !passRedeemableIds.has(op.id),
  );
  const membershipPages = activeOrderPages.filter(
    (op) => op.order_page_type === OrderPageTypes.EVENT && passRedeemableIds.has(op.id),
  );
  const coursePages = activeOrderPages.filter((op) => op.order_page_type === OrderPageTypes.COURSE);

  const allExperienceIds = [...privatePages, ...eventPages, ...membershipPages, ...coursePages].map((op) => op.id);
  const allExperiencesSelected = allExperienceIds.length > 0 && allExperienceIds.every((id) => currentIds.includes(id));

  const toggleAllExperiences = () => {
    if (allExperiencesSelected) {
      setCurrentIds([]);
    } else {
      const next = [...new Set([...currentIds, ...allExperienceIds])];
      setCurrentIds(next);
    }
  };

  const toggleSectionPages = (pageIds: string[]) => {
    const allSelected = pageIds.every((id) => currentIds.includes(id));
    if (allSelected) {
      const next = currentIds.filter((id) => !pageIds.includes(id));
      setCurrentIds(next);
    } else {
      const next = [...new Set([...currentIds, ...pageIds])];
      setCurrentIds(next);
    }
  };

  const renderSection = (titleKey: string, pages: OrderPageData[]) => {
    const pageIds = pages.map((op) => op.id);
    const allInSectionSelected = pageIds.every((id) => currentIds.includes(id));
    const groups = groupPagesByLocation(pages, account, language);
    return (
      <>
        <ListItem sx={{ pb: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Typography variant="overline" color="text.secondary">
              {t(titleKey)}
            </Typography>
            {pages.length > 1 && (
              <Button
                size="small"
                onClick={() => toggleSectionPages(pageIds)}
                sx={{ textTransform: 'none', py: 0, minWidth: 0, color: 'text.secondary' }}>
                <Typography variant="caption">
                  {allInSectionSelected ? t('calendar.filter.deselect_all') : t('calendar.filter.select_all')}
                </Typography>
              </Button>
            )}
          </Box>
        </ListItem>
        <Grid container mt={-1}>
          {groups.map((group) => (
            <React.Fragment key={group.locationId ?? '__no_location__'}>
              {multiLocation && group.locationName && (
                <ListItem sx={{ py: 0 }}>
                  <Typography variant="caption" color="text.disabled">
                    {group.locationName}
                  </Typography>
                </ListItem>
              )}
              {group.pages.map((op) => (
                <ListItem
                  key={op.id}
                  disablePadding
                  sx={{ paddingInlineStart: multiLocation && group.locationName ? '24px' : '8px' }}>
                  <FormControlLabel
                    sx={{ width: '100%', mx: 0, py: isDesktop ? 0 : 0.5 }}
                    label={op.label?.[language] ?? op.label?.en ?? ''}
                    control={
                      <Checkbox
                        size={isDesktop ? 'small' : 'medium'}
                        checked={currentIds.includes(op.id)}
                        onChange={(e) => toggleOrderPage(op.id, e.target.checked)}
                      />
                    }
                  />
                </ListItem>
              ))}
            </React.Fragment>
          ))}
        </Grid>
        <Divider />
      </>
    );
  };

  return (
    <>
      {allExperienceIds.length > 0 && (
        <>
          <ListItem sx={{ py: 0.5, justifyContent: 'flex-end' }}>
            <Button
              size="small"
              onClick={toggleAllExperiences}
              sx={{ textTransform: 'none', py: 0, minWidth: 0, color: 'text.secondary' }}>
              <Typography variant="caption">
                {allExperiencesSelected ? t('calendar.filter.deselect_all') : t('calendar.filter.select_all')}
              </Typography>
            </Button>
          </ListItem>
          <Divider />
        </>
      )}
      {!hideLocations && multiLocation && (
        <>
          <ListItem sx={{ pb: 0 }}>
            <Typography variant="overline" color="text.secondary">
              {t('calendar.filter.locations')}
            </Typography>
          </ListItem>
          <Grid container mt={-1}>
            {activeLocations.map((loc) => (
              <ListItem key={loc.id} disablePadding sx={{ paddingInlineStart: '8px' }}>
                <FormControlLabel
                  sx={{ width: '100%', mx: 0, py: isDesktop ? 0 : 0.5 }}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PlaceIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      {loc.name?.[language] ?? loc.name?.en ?? ''}
                    </Box>
                  }
                  control={
                    <Checkbox
                      size={isDesktop ? 'small' : 'medium'}
                      checked={isLocationChecked(loc.id)}
                      indeterminate={isLocationIndeterminate(loc.id)}
                      onChange={(e) => toggleLocation(loc.id, e.target.checked)}
                    />
                  }
                />
              </ListItem>
            ))}
          </Grid>
          {showEmployeesLocationHint && (
            <ListItem sx={{ pt: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                {t('calendar.filter.locations_employees_hint')}
              </Typography>
            </ListItem>
          )}
          <Divider />
        </>
      )}

      {privatePages.length > 0 && renderSection('calendar.filter.privates', privatePages)}
      {eventPages.length > 0 && renderSection('calendar.filter.events', eventPages)}
      {membershipPages.length > 0 && renderSection('calendar.filter.memberships', membershipPages)}
      {coursePages.length > 0 && renderSection('calendar.filter.courses', coursePages)}
    </>
  );
};
